/**
 * Replace all buy buttons to redirect to /pages/checkout
 * Maintains cart functionality while redirecting to custom checkout page
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // 1. Intercept product form submissions (add to cart buttons)
    interceptProductForms();
    
    // 2. Intercept checkout button clicks
    interceptCheckoutButtons();
    
    // 3. Intercept discount form submissions
    interceptDiscountForms();
    
    // 4. Intercept payment buttons (Shop Pay, Apple Pay, etc.)
    interceptPaymentButtons();
    
    // 5. Handle cart drawer checkout buttons
    interceptCartDrawerButtons();
  }

  /**
   * Intercept product form submissions
   * After adding to cart, redirect to /pages/checkout
   */
  function interceptProductForms() {
    let redirectAfterAdd = false;
    let addToCartForm = null;

    // Intercept the fetch call for adding to cart
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a cart add request
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/add.js'))) {
        // Mark that we should redirect after successful add
        redirectAfterAdd = true;
        addToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
        
        return originalFetch.apply(this, args).then(response => {
          // Clone the response so we can read it
          const clonedResponse = response.clone();
          
          // Check if the response is successful
          if (response.ok) {
            clonedResponse.json().then(data => {
              // If item was successfully added
              if (data && !data.status && redirectAfterAdd) {
                redirectAfterAdd = false;
                
                // Redirect after a short delay to allow cart to update
                setTimeout(() => {
                  window.location.href = '/pages/checkout';
                }, 300);
              }
            }).catch(() => {
              // If JSON parsing fails, continue normally
              redirectAfterAdd = false;
            });
          } else {
            redirectAfterAdd = false;
          }
          
          return response;
        }).catch(() => {
          redirectAfterAdd = false;
          throw arguments[0];
        });
      }
      
      // For other requests, use original fetch
      return originalFetch.apply(this, args);
    };

    // Intercept window.location assignments using a proxy approach
    // Note: We can't override window.location.href directly, so we'll intercept at the form level

    // Also listen for cart update events via pub/sub
    if (window.PUB_SUB_EVENTS && window.subscribe) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function(event) {
        if (event && event.source === 'product-form' && redirectAfterAdd) {
          redirectAfterAdd = false;
          // Redirect after cart update
          setTimeout(() => {
            window.location.href = '/pages/checkout';
          }, 300);
        }
      });
    }
  }

  /**
   * Intercept checkout button clicks
   * Redirect to /pages/checkout instead of default Shopify checkout
   */
  function interceptCheckoutButtons() {
    function setupCheckoutButton(button) {
      if (button.hasAttribute('data-checkout-intercepted')) {
        return; // Already intercepted
      }
      
      button.setAttribute('data-checkout-intercepted', 'true');
      
      // Use capture phase to intercept before other handlers
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Redirect to custom checkout
        window.location.href = '/pages/checkout';
        return false;
      }, true);
    }

    // Find all checkout buttons
    const checkoutSelectors = [
      'button[name="checkout"]',
      'button#checkout',
      'button#CartDrawer-Checkout',
      'button.cart__checkout-button',
      'form[action*="/checkout"] button[type="submit"]',
      '#cart-notification-form button[name="checkout"]',
      'cart-drawer button[name="checkout"]',
      'cart-drawer button#checkout',
      'cart-drawer button#CartDrawer-Checkout',
      'cart-notification button[name="checkout"]'
    ];

    // Set up existing buttons
    checkoutSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(setupCheckoutButton);
    });

    // Intercept form submissions for cart forms - use capture phase
    function setupCartForm(cartForm) {
      if (cartForm.hasAttribute('data-checkout-form-intercepted')) {
        return;
      }
      cartForm.setAttribute('data-checkout-form-intercepted', 'true');
      
      // Override form action to prevent default checkout
      const originalAction = cartForm.action;
      if (originalAction && originalAction.includes('/checkout')) {
        // Change action to prevent form submission to default checkout
        cartForm.setAttribute('data-original-action', originalAction);
        cartForm.action = 'javascript:void(0)';
      }
      
      cartForm.addEventListener('submit', function(e) {
        // Check if the submit was triggered by a checkout button
        const submitButton = document.activeElement;
        const formData = new FormData(cartForm);
        
        if (submitButton && (
          submitButton.name === 'checkout' || 
          submitButton.id === 'checkout' || 
          submitButton.id === 'CartDrawer-Checkout' ||
          formData.has('checkout')
        )) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          window.location.href = '/pages/checkout';
          return false;
        }
      }, true);
    }

    const cartForms = document.querySelectorAll('form#cart, form[name="cart"], form#cart-notification-form, form#CartDrawer-Form');
    cartForms.forEach(setupCartForm);

    // Also intercept mousedown and keydown events on checkout buttons as backup
    function setupBackupInterceptors(button) {
      if (button.hasAttribute('data-backup-intercepted')) {
        return;
      }
      button.setAttribute('data-backup-intercepted', 'true');
      
      // Intercept mousedown (fires before click)
      button.addEventListener('mousedown', function(e) {
        if (e.button === 0) { // Left mouse button
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            window.location.href = '/pages/checkout';
          }, 0);
        }
      }, true);
      
      // Intercept Enter key
      button.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = '/pages/checkout';
        }
      }, true);
    }

    // Set up backup interceptors for all checkout buttons
    checkoutSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(setupBackupInterceptors);
    });
  }

  /**
   * Intercept discount form submissions
   * Redirect to /pages/checkout with discount code
   */
  function interceptDiscountForms() {
    const discountForms = document.querySelectorAll('form[action*="discount"], form.cart-discount-form');
    
    discountForms.forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const discountInput = form.querySelector('[name="cart-discount-field"], [name="discount"]');
        const discountValue = discountInput ? discountInput.value.trim() : '';
        
        if (discountValue) {
          // Redirect to custom checkout with discount parameter
          window.location.href = `/pages/checkout?discount=${encodeURIComponent(discountValue)}`;
        } else {
          window.location.href = '/pages/checkout';
        }
      }, true);
    });

    // Override the handleDiscountForm function if it exists
    if (window.handleDiscountForm) {
      window.handleDiscountForm = function(e) {
        e.preventDefault();
        const discountInput = e.target.querySelector('[name=cart-discount-field]');
        const discountValue = discountInput ? discountInput.value.trim() : '';
        
        if (discountValue) {
          window.location.href = `/pages/checkout?discount=${encodeURIComponent(discountValue)}`;
        } else {
          window.location.href = '/pages/checkout';
        }
      };
    }
  }

  /**
   * Intercept payment buttons (Shop Pay, Apple Pay, Google Pay, etc.)
   * These are typically in dynamic checkout buttons
   */
  function interceptPaymentButtons() {
    // Find all payment buttons
    const paymentButtons = document.querySelectorAll(
      '[data-shopify="payment-button"], ' +
      '.shopify-payment-button, ' +
      'button[data-testid*="checkout"], ' +
      'div[data-testid*="checkout"] button'
    );

    paymentButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = '/pages/checkout';
      }, true);
    });

    // Also intercept clicks on payment button containers
    const paymentContainers = document.querySelectorAll('.shopify-payment-button__button, [data-shopify="payment-button"]');
    paymentContainers.forEach(container => {
      container.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = '/pages/checkout';
      }, true);
    });
  }

  /**
   * Intercept cart drawer checkout buttons and dynamically added buttons
   */
  function interceptCartDrawerButtons() {
    function setupCheckoutButton(button) {
      if (button.hasAttribute('data-checkout-intercepted')) {
        return; // Already intercepted
      }
      
      button.setAttribute('data-checkout-intercepted', 'true');
      
      // Use capture phase to intercept before other handlers
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        window.location.href = '/pages/checkout';
        return false;
      }, true);
      
      // Backup: intercept mousedown
      button.addEventListener('mousedown', function(e) {
        if (e.button === 0) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            window.location.href = '/pages/checkout';
          }, 0);
        }
      }, true);
    }

    function setupCartForm(cartForm) {
      if (cartForm.hasAttribute('data-checkout-form-intercepted')) {
        return;
      }
      cartForm.setAttribute('data-checkout-form-intercepted', 'true');
      
      // Override form action to prevent default checkout
      const originalAction = cartForm.action;
      if (originalAction && originalAction.includes('/checkout')) {
        // Change action to prevent form submission to default checkout
        cartForm.setAttribute('data-original-action', originalAction);
        cartForm.action = 'javascript:void(0)';
      }
      
      cartForm.addEventListener('submit', function(e) {
        const submitButton = document.activeElement;
        const formData = new FormData(cartForm);
        
        if (submitButton && (
          submitButton.name === 'checkout' || 
          submitButton.id === 'checkout' || 
          submitButton.id === 'CartDrawer-Checkout' ||
          formData.has('checkout')
        )) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          window.location.href = '/pages/checkout';
          return false;
        }
      }, true);
    }

    // Use MutationObserver to catch dynamically added buttons and forms
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is a checkout button
            if (node.tagName === 'BUTTON' && 
                (node.name === 'checkout' || 
                 node.id === 'checkout' || 
                 node.id === 'CartDrawer-Checkout' ||
                 node.classList.contains('cart__checkout-button'))) {
              setupCheckoutButton(node);
            }
            
            // Check if the node is a cart form
            if (node.tagName === 'FORM' && (
              node.id === 'CartDrawer-Form' ||
              node.id === 'cart' ||
              node.name === 'cart' ||
              node.id === 'cart-notification-form'
            )) {
              setupCartForm(node);
            }
            
            // Check for checkout buttons in added nodes
            if (node.querySelectorAll) {
              const checkoutButtons = node.querySelectorAll(
                'button[name="checkout"], button#checkout, button#CartDrawer-Checkout, button.cart__checkout-button'
              );
              checkoutButtons.forEach(setupCheckoutButton);
              
              // Check for cart forms in added nodes
              const cartForms = node.querySelectorAll(
                'form#CartDrawer-Form, form#cart, form[name="cart"], form#cart-notification-form'
              );
              cartForms.forEach(setupCartForm);
            }
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Also handle direct navigation to /checkout
  if (window.location.pathname === '/checkout' || window.location.pathname.includes('/checkout')) {
    // Only redirect if it's the default checkout, not our custom one
    if (!window.location.pathname.includes('/pages/checkout')) {
      window.location.href = '/pages/checkout' + window.location.search;
    }
  }
})();

